// ** React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Imports
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  IconButton,
  useTheme
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

// ** Icon Imports
import Icon from "components/icon";
import MuiArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CostIcon from "../../../assets/WorkRequirements/CostIcon.png";
import PriceIcon from "../../../assets/WorkRequirements/PriceIcon.png";
import ProfitIcon from "../../../assets/WorkRequirements/ProfitIcon.png";
import ManhoursIcon from "../../../assets/WorkRequirements/MahoursIcon.png";
import CoinIcon from 'images/CoinIcon.svg';

// ** Custom Component Imports
import CustomChip from "components/mui/chip";

// ** Component Imports
import Service from "./Service";

// ** Logic Imports
import { GetBudgetedSummary } from "../logic/GetBudgettedSummary";
import { formatUSCurrency } from "components/CurrencyFormat";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} placement="top-end" arrow />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#ddeefe",
    border: "1px solid #E1E4EA",
    whiteSpace: "nowrap",
    maxWidth: "unset",
    minWidth: "auto",
    padding: "8px 12px",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#ddeefe",
  },
}));

const ArrowDropDownIcon = styled(MuiArrowDropDownIcon)({
  fontSize: "20px",
  width: "20px",
  height: "20px",
});

const Bundle = ({ 
  entity,
  openBundle,
  openService,
  handleToggleBundle,
  workRequirementList,
  handleWorkRequirementToggle,
  handleAddInstruction,
  handleRemoveInstruction,
  handleToggleAlert,
  newInstruction,
  setNewInstruction,
  UpdateWorkRequirement,
  handleWorkRequirementChange,
  handleChange,
  PropertyAreas,  
  fetchPropertyAreas,
  handleOpenWorkRequirementUploadDlg,
  handleUploadAttachment,
  showConfirmationAttachmentDelete,
  fetchAttachments,
  handleDownloadAttachment,
  attachmentMap,
  method,
  showConfirmationWorkRequirementDelete,
  showConfirmationBundleDelete,
  rules,
  setLoading,
  toast,
  handleOpenScheduleServiceDlg,
  fetchWorkRequirements,
  requestID,
  LineItemTotalProps,
  fetchRequests,
  lineItemChanged,
  setLineItemChanged
}) => {  
  const theme = useTheme();

  // Group work requirements by bundle
  const [summaries, setSummaries] = useState([]);
  const navigate = useNavigate();
  
  const groupedBundles = workRequirementList?.reduce((acc, workReq) => {
    if (workReq.bundleID > 0) {
      const existingBundle = acc.find(b => b.bundleID === workReq.bundleID);
      
      if (existingBundle) {
        existingBundle.services.push(workReq);
      } else {
        acc.push({
          bundleID: workReq.bundleID,
          bundleName: workReq.bundleName,
          services: [workReq]
        });
      }
    }
    return acc;
  }, []);

  useEffect(() => {
    const fetchAllSummaries = async () => {
      const newSummaries = {};
      for (const bundle of groupedBundles) {
        const result = await GetBudgetedSummary(bundle.bundleID, requestID, 0, navigate);
        newSummaries[bundle.bundleID] = Array.isArray(result) ? result : [result];
      }
      setSummaries(newSummaries);
    };
    
    if (groupedBundles?.length) {
      fetchAllSummaries();
    }
  }, [workRequirementList, lineItemChanged]);

  return (
    <List component="nav" aria-label="main mailbox">
      {groupedBundles.length > 0 ? (
        groupedBundles.map((bundle, index) => {
          const summary = summaries[bundle.bundleID] || [];

          return (
            <div key={`bundle-${bundle.bundleID}`}>
              <ListItem
                disablePadding
                sx={{ borderBottom: "1px solid #E1E4EA" }}
              >
                <ListItemButton
                  onClick={() => handleToggleBundle(index, openBundle[index])}
                >
                  <Icon
                    icon={
                      openBundle[index]
                        ? "tabler:chevron-down"
                        : "tabler:chevron-right"
                    }
                  />
                  <ListItemText
                    sx={{ ml: 4 }}
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          alignItems: { xs: "flex-start", md: "center" },
                          gap: { xs: 1, md: 4 },
                        }}
                      >
                        <Typography variant="body1">
                          {bundle?.bundleName || `Bundle ${bundle.bundleID}`}
                        </Typography>
                        <CustomChip
                          skin="dark"
                          color="main"
                          sx={{
                            height: 20,
                            minWidth: 60,
                            padding: "0 6px",
                            fontSize: "0.75rem",
                            display: "flex",
                            alignItems: "center",
                            borderRadius: "4px",
                          }}
                          label={
                            <Typography
                              variant="caption"
                              color="white"
                              sx={{
                                fontSize: "0.65rem",
                                lineHeight: 1,
                                textAlign: "center",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              BUNDLE
                            </Typography>
                          }
                          size="small"
                        />
                      </Box>
                    }
                  />
                </ListItemButton>
                <Box sx={{ display: "flex", gap: 2 }}>
                  {(entity === "estimate" ||
                    entity === "estimate-detail" ||
                    entity === "workOrder" ||
                    entity === "workOrder-detail") && (
                    <HtmlTooltip
                      title={
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: { xs: "flex-start", md: "center" },
                            gap: 2,
                            color: "#FFCA28",
                          }}
                        >
                          <CustomChip
                            skin="light"
                            color="info"
                            icon={<img src={CostIcon} alt="Cost" />}
                            label={
                              <Typography variant="body2" color="info.main">
                                Cost{" "}
                                <strong>
                                  {formatUSCurrency(summary[0]?.totalCost || 0)}
                                </strong>
                              </Typography>
                            }
                          />
                          <CustomChip
                            skin="light"
                            color="info"
                            icon={<img src={PriceIcon} alt="Price" />}
                            label={
                              <Typography variant="body2" color="info.main">
                                Price{" "}
                                <strong>
                                  {formatUSCurrency(
                                    summary[0]?.totalPrice || 0
                                  )}
                                </strong>
                              </Typography>
                            }
                          />
                          <CustomChip
                            skin="light"
                            color="info"
                            icon={<img src={ProfitIcon} alt="Profit" />}
                            label={
                              <Typography variant="body2" color="info.main">
                                Profit{" "}
                                <strong>
                                  {formatUSCurrency(
                                    summary[0]?.totalProfit || 0
                                  )}
                                </strong>
                              </Typography>
                            }
                          />
                          <CustomChip
                            skin="light"
                            color="info"
                            icon={<img src={ManhoursIcon} alt="Manhours" />}
                            label={
                              <Typography variant="body2" color="info.main">
                                Manhours{" "}
                                <strong>
                                  {summary[0]?.totalManHours || 0}h
                                </strong>
                              </Typography>
                            }
                          />
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          p: "0px !important",
                          backgroundColor: "#EEF2FF",
                          borderRadius: 2,
                          cursor: "pointer",
                          display: "inline-flex", // Ensures proper alignment
                          "&:hover": {
                            backgroundColor: "#E0E7FF", // Optional hover effect
                          },
                        }}
                      >
                        <img src={CoinIcon} width="35" height="35" />
                      </Box>
                    </HtmlTooltip>
                  )}
                </Box>
                {/* <IconButton color='error' onClick={() => { showConfirmationWorkRequirementDelete(workReq.workReqID, bundle) }}> */}
                <IconButton
                  color="error"
                  onClick={() => showConfirmationBundleDelete(bundle)}
                >
                  <Icon icon="tabler:trash" />
                </IconButton>
              </ListItem>
              <Collapse
                in={openBundle[index]}
                timeout="auto"
                sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#F6F7FA" }}
              >
                <Grid container spacing={4} p={{ xs: 1, sm: 4 }} sx={{ maxWidth: "100%", backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#F6F7FA'}}>
                  <Grid
                    item
                    xs={12}
                    sx={{ maxWidth: "100%", overflowX: "auto" }}
                  >
                    {bundle.services.map((service, serviceIndex) => (
                      <Service
                        key={`service-${service.workReqID}`}
                        entity={entity}
                        isChild={true}
                        method={method}
                        workRequirementList={[service]} // Pass single service as array
                        openService={openService}
                        handleWorkRequirementToggle={
                          handleWorkRequirementToggle
                        }
                        handleAddInstruction={handleAddInstruction}
                        handleRemoveInstruction={handleRemoveInstruction}
                        handleToggleAlert={handleToggleAlert}
                        newInstruction={newInstruction}
                        setNewInstruction={setNewInstruction}
                        handleWorkRequirementChange={
                          handleWorkRequirementChange
                        }
                        handleChange={handleChange}
                        UpdateWorkRequirement={UpdateWorkRequirement}
                        PropertyAreas={PropertyAreas}
                        fetchPropertyAreas={fetchPropertyAreas}
                        handleOpenWorkRequirementUploadDlg={
                          handleOpenWorkRequirementUploadDlg
                        }
                        handleUploadAttachment={handleUploadAttachment}
                        showConfirmationAttachmentDelete={
                          showConfirmationAttachmentDelete
                        }
                        fetchAttachments={fetchAttachments}
                        handleDownloadAttachment={handleDownloadAttachment}
                        attachmentMap={attachmentMap}
                        showConfirmationWorkRequirementDelete={
                          showConfirmationWorkRequirementDelete
                        }
                        rules={rules}
                        setLoading={setLoading}
                        toast={toast}
                        bundle={bundle}
                        handleOpenScheduleServiceDlg={
                          handleOpenScheduleServiceDlg
                        }
                        fetchWorkRequirements={fetchWorkRequirements}
                        requestID={requestID}
                        LineItemTotalProps={LineItemTotalProps}
                        fetchRequests={fetchRequests}
                        lineItemChanged={lineItemChanged}
                        setLineItemChanged={setLineItemChanged}
                      />
                    ))}
                  </Grid>
                </Grid>
              </Collapse>
            </div>
          );}
        )
      ) : (
        <></>
      )}
    </List>
  );
};

export default Bundle;